import * as Sentry from '@sentry/vue';

import { useAppConfigStore } from '@/piniaStores/appConfig/appConfig.store';
import { isHttpError } from '@/utils/errors';

import { useAuthStore } from './piniaStores/auth/auth.store';
import { useUserStore } from './piniaStores/user/user.store';

export function loadSentry() {
  const appConfigStore = useAppConfigStore();

  if (
    !import.meta.env.VITE_SENTRY_ENABLED ||
    window.Cypress ||
    window?.navigator?.userAgent === 'cypress-test' ||
    appConfigStore.hasSentryLoaded ||
    localStorage.getItem('mpfe_is_dev') === 'true'
  ) {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: appConfigStore.environment,
    release: appConfigStore.release,
    normalizeDepth: 10, // prevents nested propsData from being trimmed to "[Object]" or "[Array]"
    trackComponents: true,
    attachProps: true,
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications.', // This appears to be thrown by a 3rd-party script
      'a.replaceChildren is not a function.', // This is only throwing on old safari versions that we don't support
      't.mediaQueryList.addEventListener is not a function.', // This is only throwing on old safari versions that we don't support
    ],
    beforeSend(event, hint) {
      const error = hint?.originalException;

      if (isHttpError(error) && error.response && [401].includes(error.response.status)) {
        return null;
      }

      if (isHttpError(error) && error.config?.baseURL && error.config.url) {
        // Errors from our internal API are tracked by Sentry in the backend codebase, so we should prevent them from being tracked again here
        if (error.config.baseURL?.indexOf('api/internal/') >= 0 || error.config.url?.indexOf('api/internal/') >= 0) {
          return null;
        }
      }

      return event;
    },
  });

  Sentry.configureScope((scope) => {
    scope.setTag('mpfe_release', import.meta.env.VITE_RELEASE_VERSION);
  });

  appConfigStore.hasSentryLoaded = true;
}

export function addSentryUserData() {
  const appConfigStore = useAppConfigStore();

  if (
    !import.meta.env.VITE_SENTRY_ENABLED ||
    window.Cypress ||
    window?.navigator?.userAgent === 'cypress-test' ||
    appConfigStore.hasSentryLoaded ||
    localStorage.getItem('mpfe_is_dev') === 'true'
  ) {
    return;
  }

  const userStore = useUserStore();
  const authStore = useAuthStore();

  Sentry.configureScope((scope) => {
    scope.setTag('company_id', userStore.user.activeCompanyId);
    scope.setTag('company_slug', userStore.userActiveCompany?.slug);
    scope.setTag('user_locale', userStore.user.language);
    scope.setTag('user_pk', userStore.user.id);
    scope.setUser({ id: authStore.userId });
  });
}
